import type { LampTone } from '../../ui/StatusLamp.tsx';
import type { TargetProbeLegRecord, TargetProbeTripReason } from '@nadoutong/contracts';
import type { I18nKey } from '../../i18n/index.ts';
import { skuNamespaceKey } from '../../components/SkuGroupPicker.tsx';

export type LegLamp = 'ok' | 'shadow' | 'tripped' | 'manual' | 'unprobed' | 'immune' | 'failing';

export function minutesBefore(iso: string, nowMs: number): number | null {
  const parsed = Date.parse(iso);
  if (!Number.isFinite(parsed)) return null;
  return Math.max(0, Math.floor((nowMs - parsed) / 60_000));
}

export function minutesUntil(iso: string | null, nowMs: number): number | null {
  if (iso === null) return null;
  const parsed = Date.parse(iso);
  if (!Number.isFinite(parsed)) return null;
  return Math.max(0, Math.ceil((parsed - nowMs) / 60_000));
}

export function legIsAbnormal(lamp: LegLamp): boolean {
  return lamp !== 'ok';
}

export function legMatchesQuery(leg: TargetProbeLegRecord, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (q.length === 0) return true;
  return `${leg.sku_name}\n${leg.upstream_name}\n${leg.physical_model}\n${leg.upstream_channel_id}`
    .toLowerCase()
    .includes(q);
}

/** Whole minutes still covered. Null once `immune_until` is absent or past. */
export function immunityMinutesRemaining(iso: string | null, nowMs: number): number | null {
  if (iso === null) return null;
  const parsed = Date.parse(iso);
  if (!Number.isFinite(parsed) || parsed <= nowMs) return null;
  return Math.max(1, Math.ceil((parsed - nowMs) / 60_000));
}

/**
 * How the SKU that owns a leg is configured. `probesOn` = probe mode is not
 * off (the row has a next-probe time that means something); `armed` = at
 * least one mechanism is in act mode, so a `tripped` row actually demotes
 * the leg. A tripped row on an unarmed SKU is a leftover, not a live removal.
 */
export interface LegSkuArming {
  readonly probesOn: boolean;
  readonly armed: boolean;
  /** SKU probe criteria; null when the SKU row is unknown. */
  readonly tripFailedRounds: number | null;
  readonly recoverOkRounds: number | null;
}

export const LEG_SKU_UNKNOWN: LegSkuArming = {
  probesOn: true,
  armed: true,
  tripFailedRounds: null,
  recoverOkRounds: null,
};

/**
 * Highest routing weight across members; 0 means the leg never takes the first
 * pick. `null` when the weight is not known: a gateway predating #2619 omits
 * `members[].weight`, and reading that as 0 would claim 「不首发」 about a leg
 * that may well be the first pick. 0 is returned only when EVERY member
 * carries an explicit 0.
 */
export function legMaxWeight(leg: TargetProbeLegRecord): number | null {
  const weights = leg.members.map((member): unknown => member.weight)
    .filter((weight): weight is number => typeof weight === 'number' && Number.isFinite(weight));
  if (weights.length === 0) return null;
  const max = Math.max(...weights);
  if (max > 0) return max;
  return weights.length === leg.members.length ? 0 : null;
}

/** Identity that reads the same on the leg row face: upstream · model · channel. */
export function legFaceKey(leg: TargetProbeLegRecord): string {
  return `${leg.upstream_name}\u0000${leg.physical_model}\u0000${leg.upstream_channel_id}`;
}

/** How a leg row must extend its sub line to be told apart from look-alikes. */
export interface LegLookalike {
  /** Two or more legs of the SKU read upstream · model · channel alike: print account / bucket tails. */
  readonly tails: boolean;
  /** Those legs sit in different regions (JP / CN slices of one supply since 0081): print the region. */
  readonly region: boolean;
}

export const LEG_UNIQUE: LegLookalike = { tails: false, region: false };

/** Per face key: whether rows sharing it need supply tails and / or a region tag. */
export function legLookalikes(legs: readonly TargetProbeLegRecord[]): ReadonlyMap<string, LegLookalike> {
  const byKey = new Map<string, TargetProbeLegRecord[]>();
  for (const leg of legs) byKey.set(legFaceKey(leg), [...(byKey.get(legFaceKey(leg)) ?? []), leg]);
  const out = new Map<string, LegLookalike>();
  for (const [key, group] of byKey) {
    if (group.length < 2) continue;
    out.set(key, { tails: true, region: new Set(group.map((leg) => leg.region)).size >= 2 });
  }
  return out;
}

/** Anchor member for probe-now / reset: an enabled one first, else the first. */
export function legAnchorMember(leg: TargetProbeLegRecord): TargetProbeLegRecord['members'][number] | undefined {
  return leg.members.find((member) => member.enabled) ?? leg.members[0];
}

export function legIsManual(leg: TargetProbeLegRecord): boolean {
  return leg.members.length > 0 && leg.members.every((member) => !member.enabled);
}

export function legLamp(leg: TargetProbeLegRecord, nowMs: number): LegLamp {
  if (legIsManual(leg)) return 'manual';
  if (leg.tripped) return 'tripped';
  if (leg.shadow_trip_reason !== null) return 'shadow';
  if (immunityMinutesRemaining(leg.immune_until, nowMs) !== null) return 'immune';
  if (leg.last_probe_at === null) return 'unprobed';
  if (legLatestProbeFailed(leg)) return 'failing';
  return 'ok';
}

/**
 * Latest probe result was not a success, but the leg is below the trip line
 * (one failed shot does not trip by default). Must not read as healthy.
 */
export function legLatestProbeFailed(leg: TargetProbeLegRecord): boolean {
  const code = leg.last_status_code;
  if (code !== null) return code < 200 || code > 299;
  if (leg.last_error_excerpt === null) return false;
  if (leg.last_ok_at === null || leg.last_probe_at === null) return true;
  return Date.parse(leg.last_ok_at) < Date.parse(leg.last_probe_at);
}

export function legLampTone(lamp: LegLamp): LampTone {
  switch (lamp) {
    case 'ok':
      return 'ok';
    case 'shadow':
    case 'failing':
      return 'warn';
    case 'tripped':
      return 'bad';
    case 'immune':
      return 'signal';
    case 'manual':
    case 'unprobed':
      return 'off';
  }
}

export function tripReasonKey(reason: TargetProbeTripReason | null): I18nKey {
  switch (reason) {
    case 'chain':
      return 'tenant_routing.probe.reason.chain';
    case 'jev':
      return 'tenant_routing.probe.reason.jev';
    case 'probe':
    case null:
      return 'tenant_routing.probe.reason.probe';
  }
}

export interface LegSkuGroup {
  readonly skuId: string;
  readonly skuName: string;
  readonly legs: readonly TargetProbeLegRecord[];
}

export interface LegNamespaceGroup {
  readonly namespace: string;
  readonly skus: readonly LegSkuGroup[];
}

export function groupLegs(legs: readonly TargetProbeLegRecord[]): readonly LegNamespaceGroup[] {
  const byNamespace = new Map<string, Map<string, TargetProbeLegRecord[]>>();
  const skuNames = new Map<string, string>();
  for (const leg of legs) {
    const namespace = skuNamespaceKey(leg.sku_name);
    const skus = byNamespace.get(namespace) ?? new Map<string, TargetProbeLegRecord[]>();
    const existing = skus.get(leg.sku_id) ?? [];
    skus.set(leg.sku_id, [...existing, leg]);
    byNamespace.set(namespace, skus);
    skuNames.set(leg.sku_id, leg.sku_name);
  }
  const namespaces = [...byNamespace.keys()].sort((left, right) => {
    if (left === '') return 1;
    if (right === '') return -1;
    return left.localeCompare(right);
  });
  return namespaces.map((namespace) => {
    const skus = byNamespace.get(namespace) ?? new Map<string, TargetProbeLegRecord[]>();
    const skuIds = [...skus.keys()].sort((left, right) =>
      (skuNames.get(left) ?? left).localeCompare(skuNames.get(right) ?? right));
    return {
      namespace,
      skus: skuIds.map((skuId) => ({
        skuId,
        skuName: skuNames.get(skuId) ?? skuId,
        legs: [...(skus.get(skuId) ?? [])].sort((left, right) =>
          left.upstream_name.localeCompare(right.upstream_name)
          || left.upstream_channel_id.localeCompare(right.upstream_channel_id)),
      })),
    };
  });
}

export const IMMUNITY_CHOICES = ['0', '5', '10', '30', '60'] as const;
export type ImmunityChoice = (typeof IMMUNITY_CHOICES)[number];

export function isImmunityChoice(value: string): value is ImmunityChoice {
  return IMMUNITY_CHOICES.some((choice) => choice === value);
}

export function immunityMinutes(choice: ImmunityChoice): number {
  switch (choice) {
    case '0':
      return 0;
    case '5':
      return 5;
    case '10':
      return 10;
    case '30':
      return 30;
    case '60':
      return 60;
  }
}
